// lib/features/search/search.hook.ts
import {
  useAppDispatch,
  useAppSelector,
} from "@/app/lib/features/redux/redux.hook";
import { useCallback, useMemo, useEffect } from "react";
import {
  getHomeSearchList,
  saveSearch as saveSearchThunk,
  clearSearchHistory as clearSearchHistoryThunk,
  bulkDeleteSearches as bulkDeleteSearchesThunk,
  getSearchResults as getSearchResultsThunk,
  getFavoriteSearches as getFavoriteSearchesThunk,
  getSearchSuggestions as getSearchSuggestionsThunk,
  getImageSearchResults as getImageSearchResultsThunk,
} from "./search.thunk";
import {
  removeSearchItem,
  removeFavoriteSearchItem,
  loadLocalSearches,
  addLocalSearch,
  removeLocalSearch,
  clearLocalSearches,
  clearSuggestions,
  resetSearchResults as resetSearchResultsAction,
  loadInitialSearchResults,
  openPhotoSearch,
  closePhotoSearch,
} from "./search.slice";
import {
  SaveSearchRequest,
  BulkDeleteSearchRequest,
  PastSearchSection,
  PopularSearchSection,
  SpecialProductsSection,
  PastSearchItem,
  SearchResultsRequest,
  SearchResultsData,
  GetFavoriteSearchesRequest,
  GetSearchSuggestionsRequest,
} from "./search.types";
import { isError, isIdle, isLoading, isSuccess } from "@/app/utils/asyncStatus";
import useAuth from "../auth/auth.hook";

const useSearch = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuth();
  const {
    data: searchSections,
    placeholders,
    status,
    error,
    saveStatus,
    clearStatus,
    deleteStatus,
    localSearches,
    searchResults,
    searchResultsStatus,
    searchResultsError,
    searchResultsQuery,
    favoriteSearches,
    favoriteSearchesStatus,
    favoriteSearchesError,
    suggestions,
    suggestionsStatus,
    suggestionsError,
    isPhotoSearchOpen,
  } = useAppSelector((store) => store.search);

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(loadLocalSearches());
    }
  }, [dispatch, isAuthenticated]);

  const fetchHomeSearchList = useCallback(async () => {
    const result = await dispatch(getHomeSearchList());
    return result.meta.requestStatus === "fulfilled";
  }, [dispatch]);

  const saveSearch = useCallback(
    async (data: SaveSearchRequest) => {
      if (isAuthenticated) {
        const result = await dispatch(saveSearchThunk(data));
        if (result.meta.requestStatus === "fulfilled") {
          await dispatch(getHomeSearchList());
        }
        return result.meta.requestStatus === "fulfilled";
      } else {
        dispatch(addLocalSearch(data.search_term));
        return true;
      }
    },
    [dispatch, isAuthenticated],
  );

  const clearHistory = useCallback(async () => {
    if (isAuthenticated) {
      const result = await dispatch(clearSearchHistoryThunk());
      return result.meta.requestStatus === "fulfilled";
    } else {
      dispatch(clearLocalSearches());
      return true;
    }
  }, [dispatch, isAuthenticated]);

  const deleteSingleSearch = useCallback(
    async (id: number) => {
      if (isAuthenticated) {
        const result = await dispatch(bulkDeleteSearchesThunk({ ids: [id] }));
        if (result.meta.requestStatus === "fulfilled") {
          dispatch(removeSearchItem(id));
        }
        return result.meta.requestStatus === "fulfilled";
      } else {
        dispatch(removeLocalSearch(id));
        return true;
      }
    },
    [dispatch, isAuthenticated],
  );

  const deleteMultipleSearches = useCallback(
    async (data: BulkDeleteSearchRequest) => {
      if (isAuthenticated) {
        const result = await dispatch(bulkDeleteSearchesThunk(data));
        if (result.meta.requestStatus === "fulfilled") {
          data.ids.forEach((id) => dispatch(removeSearchItem(id)));
        }
        return result.meta.requestStatus === "fulfilled";
      } else {
        data.ids.forEach((id) => dispatch(removeLocalSearch(id)));
        return true;
      }
    },
    [dispatch, isAuthenticated],
  );

  const pastSearches = useMemo<PastSearchSection | null>(() => {
    if (isAuthenticated) {
      if (!searchSections) return null;
      return (
        (searchSections.find((s) => s.type === "list") as PastSearchSection) ||
        null
      );
    } else {
      if (localSearches.length === 0) return null;
      const items: PastSearchItem[] = localSearches.map((search) => ({
        id: search.id,
        search_term: search.search_term,
      }));
      return {
        title: "Geçmiş Aramalar",
        type: "list",
        items,
        top_button: "Temizle",
      };
    }
  }, [searchSections, localSearches, isAuthenticated]);

  const popularSearches = useMemo<PopularSearchSection | null>(() => {
    if (!searchSections) return null;
    return (
      (searchSections.find((s) => s.type === "link") as PopularSearchSection) ||
      null
    );
  }, [searchSections]);

  const specialProducts = useMemo<SpecialProductsSection | null>(() => {
    if (!searchSections) return null;
    return (
      (searchSections.find(
        (s) => s.type === "product",
      ) as SpecialProductsSection) || null
    );
  }, [searchSections]);

  const fetchSearchResults = useCallback(
    async (params: SearchResultsRequest) => {
      const result = await dispatch(getSearchResultsThunk(params));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchFavoriteSearches = useCallback(
    async (params?: GetFavoriteSearchesRequest) => {
      const result = await dispatch(getFavoriteSearchesThunk(params));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchMoreFavoriteSearches = useCallback(() => {
    if (!favoriteSearches?.pagination) return;
    const { current_page, last_page, per_page } = favoriteSearches.pagination;
    if (current_page >= last_page) return;
    dispatch(getFavoriteSearchesThunk({ page: current_page + 1, per_page }));
  }, [dispatch, favoriteSearches]);

  const bulkDeleteFavorites = useCallback(
    async (searchIds: number[]) => {
      const result = await dispatch(
        bulkDeleteSearchesThunk({ ids: searchIds }),
      );
      if (result.meta.requestStatus === "fulfilled") {
        searchIds.forEach((id) => dispatch(removeFavoriteSearchItem(id)));
      }
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchSuggestions = useCallback(
    async (params: GetSearchSuggestionsRequest) => {
      const result = await dispatch(getSearchSuggestionsThunk(params));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const resetSuggestions = useCallback(() => {
    dispatch(clearSuggestions());
  }, [dispatch]);

  const fetchImageSearchResults = useCallback(
    async (image: File) => {
      const result = await dispatch(getImageSearchResultsThunk({ image }));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const openPhotoSearchSheet = useCallback(() => {
    dispatch(openPhotoSearch());
  }, [dispatch]);

  const closePhotoSearchSheet = useCallback(() => {
    dispatch(closePhotoSearch());
  }, [dispatch]);

  return {
    searchSections,
    placeholders,
    isPhotoSearchOpen,
    openPhotoSearchSheet,
    closePhotoSearchSheet,
    pastSearches,
    popularSearches,
    specialProducts,
    localSearches,
    searchResults,
    favoriteSearches,
    suggestions,
    status,
    error,
    isIdle: isIdle(status),
    isLoading: isLoading(status),
    isSuccess: isSuccess(status),
    isError: isError(status),
    saveStatus,
    isSaving: isLoading(saveStatus),
    isSaveSuccess: isSuccess(saveStatus),
    clearStatus,
    isClearing: isLoading(clearStatus),
    isClearSuccess: isSuccess(clearStatus),
    deleteStatus,
    isDeleting: isLoading(deleteStatus),
    isDeleteSuccess: isSuccess(deleteStatus),
    searchResultsStatus,
    searchResultsError,
    searchResultsQuery,
    isSearchResultsLoading: isLoading(searchResultsStatus),
    isSearchResultsSuccess: isSuccess(searchResultsStatus),
    isSearchResultsError: isError(searchResultsStatus),
    favoriteSearchesStatus,
    favoriteSearchesError,
    isFavoriteSearchesLoading: isLoading(favoriteSearchesStatus),
    isFavoriteSearchesSuccess: isSuccess(favoriteSearchesStatus),
    isBulkDeletingFavorites: isLoading(deleteStatus),
    suggestionsStatus,
    suggestionsError,
    isSuggestionsLoading: isLoading(suggestionsStatus),
    fetchHomeSearchList,
    saveSearch,
    clearHistory,
    deleteSingleSearch,
    deleteMultipleSearches,
    fetchSearchResults,
    fetchFavoriteSearches,
    fetchMoreFavoriteSearches,
    bulkDeleteFavorites,
    fetchSuggestions,
    resetSuggestions,
    fetchImageSearchResults,
    resetSearchResults: useCallback(
      () => dispatch(resetSearchResultsAction()),
      [dispatch],
    ),
    seedSearchResults: useCallback(
      (data: SearchResultsData, query: string) =>
        dispatch(loadInitialSearchResults({ data, query })),
      [dispatch],
    ),
  };
};

export default useSearch;
